/**
 * The event-shell read: the ONE query that carries what the event chrome needs
 * — plugins (module gating) and sidebarOrder (module order) among them.
 *
 * Extracted from EventLayoutShell so surfaces outside the shell (the onboarding
 * panel's setup track) can read the SAME cache entry instead of firing a second
 * request with a different field set — and so a change to the field list can't
 * leave one of them behind.
 */
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { getEvent } from "@/lib/api/handlers/EventHandling";
import type { Event } from "@/types/Event";

export const EVENT_LAYOUT_EVENT_FIELDS =
  "plugins,archived,blocked,name,registrationType,sidebarOrder,timeZone,planType,planExpiresAt,planLocked,globalGuestLimit,globalGuestLimitEnabled,globalWaitlistEnabled,globalWaitlistLimit";

export function eventLayoutEventKey(eventId: string | null | undefined) {
  return ["eventLayout", eventId, "event"];
}

export function useEventLayoutEventQuery(eventId: string | null | undefined) {
  return useQuery({
    queryKey: eventLayoutEventKey(eventId),
    queryFn: () => getEvent(eventId as string, EVENT_LAYOUT_EVENT_FIELDS),
    enabled: !!eventId,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    // Keep the previous event's data painted while the new event loads so the
    // page Outlet doesn't blank to a spinner on every switch.
    placeholderData: keepPreviousData,
    // B2-0: the preview re-mint's cache bust leaves this read alone — `GET
    // /events/{id}` needs only a foundation cap, so its answer does not depend
    // on the preview bearer, and refetching it is what blanked the shell.
    meta: { previewScopeIndependent: true },
  });
}

/**
 * NOTE: there is deliberately no `useEventLayoutEvent()` unwrapping helper.
 * Every caller needs more than `data` — the shell distinguishes the
 * server-error branch, the setup surfaces must also reject a `keepPreviousData`
 * placeholder (that would be the PREVIOUS event's plugins). Use the query hook
 * and `isServerErr` from ErrorHandler.
 */
export type { Event };
